// messageValidation.ts
import DOMPurify from "dompurify";
// Accepts only whitelisted characters: letters, numbers, basic punctuation, spaces, and newlines
const WHITELIST_REGEX = /^[a-zA-Z0-9 .,!?'"()\-\n\r]+$/;

// Escapes HTML special characters
export function sanitizeInput(input, maxLength = 1000) {
  if (typeof input !== "string") return "";
  let trimmed = input.trim().slice(0, maxLength);
  // Only allow whitelisted characters
  if (!WHITELIST_REGEX.test(trimmed)) {
    // Remove all non-whitelisted characters
    trimmed = trimmed.replace(/[^a-zA-Z0-9 .,!?'"()\-\n\r]/g, "");
  }
  // Escape HTML entities
  return trimmed
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;")
    .replace(/\//g, "&#x2F;");
}

export const capitalizeFirstLetter = (str) => {
  if (!str) return "";
  return str.charAt(0).toUpperCase() + str.slice(1);
};


export const validateMessageBody = (value, minLength, maxLength) => {
  const trimmed = value.trim();

  // 1. Required check
  if (trimmed.length === 0) {
    return "Message body is required";
  }

  // 2. Length caps (DoS prevention)
  if (trimmed.length < minLength) {
    return `Message body must be at least ${minLength} characters`;
  }
  if (trimmed.length > maxLength) {
    return `Message body must not exceed ${maxLength} characters`;
  }

  // 3. Disallowed patterns (basic XSS payloads)
  const dangerousPatterns = [
    /<script.*?>.*?<\/script>/i,
    /javascript:/i,
    /on\w+=/i, // inline event handlers like onerror, onclick
  ];
  for (const pattern of dangerousPatterns) {
    if (pattern.test(trimmed)) {
      return "Message body contains unsafe content";
    }
  }

  // 4. Blacklisted characters (custom rule)
  const blacklistedChars = ["<", ">", "{", "}", "`", "$"]; 
  for (const char of blacklistedChars) {
    if (trimmed.includes(char)) {
      return `Message body contains disallowed character: ${char}`;
    }
  }

  // 5. Sanitization check (optional defensive step)
  const sanitized = DOMPurify.sanitize(trimmed, { ALLOWED_TAGS: [], ALLOWED_ATTR: [] });
  if (sanitized !== trimmed) {
    return "Message body contains disallowed HTML or attributes";
  }

  // ✅ Passed all checks
  return null;
};
